import React, { useEffect, useRef, useState } from 'react';

import { css, keyframes } from '@emotion/react';

const heroEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const EASE_OUT_EXPO = 'cubic-bezier(0.16, 1, 0.3, 1)';

type EntranceHeadlineProps = {
  as?: React.ElementType;
  immediate?: boolean;
  slowEnter?: boolean;
  className?: string;
  children: React.ReactNode;
} & Record<string, unknown>;

export const EntranceHeadline: React.FC<EntranceHeadlineProps> = ({
  as: Component = 'h2',
  immediate = false,
  slowEnter = false,
  className,
  children,
  ...rest
}) => {
  const ref = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(immediate);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (immediate) {
      return;
    }

    const el = ref.current;
    if (!el) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [immediate]);

  const show = visible || reducedMotion;

  return (
    <Component
      ref={ref}
      className={className}
      css={[
        !show &&
          css`
            opacity: 0;
            transform: translateY(24px);
          `,
        show &&
          !reducedMotion &&
          css`
            animation: ${heroEnter} ${slowEnter ? '1500ms' : '1100ms'}
              ${EASE_OUT_EXPO} both;
          `,
        reducedMotion &&
          css`
            opacity: 1;
            transform: none;
          `,
      ]}
      {...rest}
    >
      {children}
    </Component>
  );
};
